import { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, Loader2, Wallet as WalletIcon, CreditCard, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import CaulTopupTicker from "./CaulTopupTicker";
import gaTrack from "../lib/gaTrack";

/**
 * CAUL top-up — dual flow.
 *
 *   • Xaman (XRP wallet-to-wallet, live DEX-priced + 5% spread, 60s quote).
 *     Default tab when the user has a connected Xaman wallet + active trustline.
 *   • Stripe (card / USDC via Crypto Onramp) as the Web2 fallback.
 *     Two pricing modes: legacy fixed packages OR live-priced custom amount.
 */
export default function CaulTopupCard({ onCredited, xrplAddress, trustlineActive }) {
  const xamanReady = !!xrplAddress && !!trustlineActive;
  const [tab, setTab] = useState(xamanReady ? "xaman" : "stripe");

  // Keep tab in sync if the wallet/trustline state changes after mount
  useEffect(() => {
    setTab(xamanReady ? "xaman" : "stripe");
  }, [xamanReady]);

  return (
    <div className="parchment p-6" data-testid="caul-topup-card">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={14} className="text-secondary" strokeWidth={1.75} />
        <span className="label-mystic text-secondary">Top Up CAUL</span>
      </div>
      <p className="text-sm text-foreground/70 mb-4 max-w-xl">
        Fund your wallet at the live DEX price (5% spread). Connect Xaman to
        pay in XRP for instant on-chain delivery, or use card / USDC via
        Stripe for the credit-card route.
      </p>

      <div
        className="inline-flex rounded-sm border border-secondary/30 mb-5 overflow-hidden"
        role="tablist"
        aria-label="CAUL top-up payment method"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === "xaman"}
          onClick={() => setTab("xaman")}
          className={`px-4 py-2 text-xs uppercase tracking-[0.18em] flex items-center gap-2 transition-colors ${
            tab === "xaman"
              ? "bg-secondary text-secondary-foreground"
              : "bg-secondary/[0.04] text-foreground/70 hover:bg-secondary/[0.08]"
          }`}
          data-testid="caul-topup-tab-xaman"
        >
          <WalletIcon size={12} strokeWidth={1.75} /> Xaman (XRP)
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "stripe"}
          onClick={() => setTab("stripe")}
          className={`px-4 py-2 text-xs uppercase tracking-[0.18em] flex items-center gap-2 transition-colors ${
            tab === "stripe"
              ? "bg-secondary text-secondary-foreground"
              : "bg-secondary/[0.04] text-foreground/70 hover:bg-secondary/[0.08]"
          }`}
          data-testid="caul-topup-tab-stripe"
        >
          <CreditCard size={12} strokeWidth={1.75} /> Card / USDC
        </button>
      </div>

      {tab === "xaman" ? (
        <XamanTopupPane
          ready={xamanReady}
          xrplAddress={xrplAddress}
          onSwitchToStripe={() => setTab("stripe")}
        />
      ) : (
        <StripeTopupPane onCredited={onCredited} />
      )}

      <CaulTopupTicker />
    </div>
  );
}

// ============================================================
// Xaman pane — quote → QR → poll → delivered
// ============================================================
function XamanTopupPane({ ready, xrplAddress, onSwitchToStripe }) {
  const [amount, setAmount] = useState(1000);
  const [quote, setQuote] = useState(null); // {quoteId, caulAmount, xrpCost, livePrice, expiresAt, ...}
  const [payload, setPayload] = useState(null); // {quoteId, xamanUuid, qrPng, openInApp, expiresAt}
  const [status, setStatus] = useState(null); // server status of the quote
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());
  const pollRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const secondsLeft = useMemo(() => {
    const exp = quote?.expiresAt;
    if (!exp) return null;
    const ms = new Date(exp).getTime() - now;
    return Math.max(0, Math.ceil(ms / 1000));
  }, [quote, now]);

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  useEffect(() => () => stopPolling(), []);

  const getQuote = async () => {
    setError(null);
    setBusy(true);
    setPayload(null);
    setStatus(null);
    try {
      const { data } = await api.post("/caul/topup/quote", { caulAmount: Number(amount) });
      setQuote(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not fetch live price");
    } finally {
      setBusy(false);
    }
  };

  const initXaman = async () => {
    if (!quote) return;
    setError(null);
    setBusy(true);
    try {
      const { data } = await api.post(`/caul/topup/xaman/init/${quote.id}`);
      setPayload(data);
      gaTrack.caulTopupInitiated({ method: "xaman", caulAmount: quote.caulAmount, pricingMode: "live" });
      startPolling(quote.id);
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not create Xaman payload";
      setError(detail);
      if (e.response?.status === 410) setQuote(null); // expired
    } finally {
      setBusy(false);
    }
  };

  const startPolling = (quoteId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/caul/topup/status/${quoteId}`);
        setStatus(data);
        if (data.status === "delivered") {
          toast.success(`+${data.caulAmount} CAUL delivered on-chain.`);
          stopPolling();
        } else if (["expired", "failed-needs-refund", "failed-verification"].includes(data.status)) {
          stopPolling();
        }
      } catch (e) {
        // ignore transient errors
      }
    }, 2500);
  };

  if (!ready) {
    return (
      <div
        className="rounded-sm border border-dashed border-secondary/30 p-5 text-sm text-foreground/70"
        data-testid="caul-topup-xaman-not-ready"
      >
        <div className="font-serif text-base text-foreground mb-1">Connect Xaman first</div>
        <p className="text-xs text-foreground/60 mb-3">
          To pay in XRP you need a linked Xaman wallet with an active CAUL
          trustline. Scroll up to the <b>Xaman Wallet</b> card to connect.
          Until then you can{" "}
          <button
            type="button"
            className="underline text-secondary hover:text-secondary/80"
            onClick={onSwitchToStripe}
            data-testid="caul-topup-switch-to-stripe"
          >
            top up with a card
          </button>{" "}
          instead.
        </p>
      </div>
    );
  }

  const delivered = status?.status === "delivered";
  const awaiting = status?.status === "awaiting-signature" || status?.status === "paid";

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="caul-topup-xaman">
      <div>
        <label className="label-mystic text-secondary/80 block mb-2">CAUL amount</label>
        <div className="flex items-stretch gap-2">
          <input
            type="number"
            min={100}
            step={50}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="parchment-input flex-1 px-3 py-2 rounded-sm border border-secondary/30 bg-background text-foreground text-base"
            placeholder="1000"
            data-testid="caul-topup-amount-input"
            disabled={!!payload && awaiting}
          />
          <button
            type="button"
            onClick={getQuote}
            disabled={busy || (!!payload && awaiting) || !amount || Number(amount) <= 0}
            className="px-4 py-2 rounded-sm bg-secondary text-secondary-foreground text-xs uppercase tracking-[0.18em] hover:bg-secondary/85 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="caul-topup-get-quote-btn"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Get live quote
          </button>
        </div>
        <p className="text-[11px] text-foreground/55 mt-2">
          Min 100 CAUL. Live DEX price + 5% spread. Quote locks for 60s.
        </p>

        {quote && (
          <div className="mt-4 rounded-sm border border-secondary/30 bg-secondary/[0.04] p-4" data-testid="caul-topup-quote">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="label-mystic text-secondary/70 text-[10px]">You receive</div>
                <div className="font-serif text-2xl text-foreground" data-testid="caul-topup-quote-caul">
                  {Number(quote.caulAmount).toLocaleString()}
                  <span className="text-foreground/40 text-xs ml-1">CAUL</span>
                </div>
              </div>
              <div>
                <div className="label-mystic text-secondary/70 text-[10px]">You pay</div>
                <div className="font-serif text-2xl text-foreground" data-testid="caul-topup-quote-xrp">
                  {Number(quote.xrpCost).toFixed(6)}
                  <span className="text-foreground/40 text-xs ml-1">XRP</span>
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-[11px] text-foreground/55">
              <span data-testid="caul-topup-quote-price">
                @ {Number(quote.livePrice).toFixed(8)} XRP / CAUL · {quote.spreadPct.toFixed(1)}% spread
              </span>
              {secondsLeft !== null && (
                <span
                  className={secondsLeft <= 10 ? "text-primary" : "text-foreground/55"}
                  data-testid="caul-topup-quote-countdown"
                >
                  {secondsLeft > 0 ? `Quote expires in ${secondsLeft}s` : "Quote expired"}
                </span>
              )}
            </div>
            {!payload && (
              <button
                type="button"
                onClick={initXaman}
                disabled={busy || (secondsLeft !== null && secondsLeft <= 0)}
                className="mt-3 w-full px-4 py-2 rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em] hover:bg-primary/85 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="caul-topup-init-xaman-btn"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <WalletIcon size={12} />}
                Open in Xaman to sign
              </button>
            )}
          </div>
        )}

        {error && (
          <div className="mt-3 text-xs text-destructive" data-testid="caul-topup-error">{error}</div>
        )}
      </div>

      <div>
        {payload ? (
          <div className="rounded-sm border border-secondary/30 p-4" data-testid="caul-topup-payload">
            <div className="label-mystic text-secondary/70 text-[10px] mb-2">
              {delivered ? "Delivered" : awaiting ? "Awaiting signature" : "Scan with Xaman"}
            </div>
            {payload.qrPng && !delivered && (
              <img
                src={payload.qrPng}
                alt="Xaman QR code"
                className="w-44 h-44 bg-white p-2 mx-auto block rounded-sm"
                data-testid="caul-topup-qr"
              />
            )}
            {delivered && (
              <div className="font-serif text-xl text-secondary text-center py-6" data-testid="caul-topup-delivered">
                ✓ Delivered on-chain
              </div>
            )}
            {!delivered && payload.openInApp && (
              <a
                href={payload.openInApp}
                target="_blank"
                rel="noreferrer"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm border border-secondary/40 text-xs uppercase tracking-[0.18em] text-foreground hover:bg-secondary/[0.08]"
                data-testid="caul-topup-open-in-app"
              >
                <ExternalLink size={12} /> Open in Xaman app
              </a>
            )}
            {status && !delivered && (
              <div className="text-[11px] text-foreground/55 text-center mt-2" data-testid="caul-topup-status">
                Status: {status.status}
                {status.xamanTxHash && <div className="truncate">tx: {status.xamanTxHash}</div>}
              </div>
            )}
            {delivered && status?.caulTxHash && (
              <div className="text-[11px] text-foreground/55 text-center mt-2 break-all" data-testid="caul-topup-tx">
                CAUL tx {status.caulTxHash.slice(0, 12)}…{status.caulTxHash.slice(-8)}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-sm border border-dashed border-secondary/20 p-6 text-center text-xs text-foreground/50 h-full flex items-center justify-center">
            Your QR code will appear here once you lock a quote.
          </div>
        )}
        <p className="text-[10px] text-foreground/40 mt-2 text-center truncate">
          Wallet: {xrplAddress}
        </p>
      </div>
    </div>
  );
}

// ============================================================
// Stripe pane — packages (legacy) + custom-amount (live price)
// ============================================================
function StripeTopupPane({ onCredited }) {
  const [packages, setPackages] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [cryptoEnabled, setCryptoEnabled] = useState(true);
  const [mode, setMode] = useState("package"); // "package" | "custom"
  const [customCaul, setCustomCaul] = useState(1000);
  const [customQuote, setCustomQuote] = useState(null);
  const [customBusy, setCustomBusy] = useState(false);
  const [customError, setCustomError] = useState(null);

  useEffect(() => {
    api
      .get("/caul/topup/packages")
      .then(({ data }) => setPackages(data.packages || []))
      .catch(() => {});
  }, []);

  // On return from Stripe, poll status — credit CAUL once settled.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    setStatusBusy(true);
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts >= 6) {
        setStatusBusy(false);
        window.history.replaceState({}, "", "/wallet");
        return;
      }
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("CAUL top-up settled — your wallet is updated.");
          setStatusBusy(false);
          window.history.replaceState({}, "", "/wallet");
          if (onCredited) onCredited();
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout session expired. Try again.");
          setStatusBusy(false);
          window.history.replaceState({}, "", "/wallet");
          return;
        }
      } catch { /* ignore + retry */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [onCredited]);

  const startCheckout = async (pkgId) => {
    setLoadingId(pkgId);
    try {
      const { data } = await api.post("/checkout/init", {
        kind: "caul_topup",
        package: pkgId,
        origin: window.location.origin,
      });
      if (!data.url) throw new Error("No checkout URL");
      if (data.cryptoEnabled === false) setCryptoEnabled(false);
      gaTrack.caulTopupInitiated({ method: "stripe", caulAmount: (packages.find((p) => p.id === pkgId) || {}).caul, pricingMode: "package" });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoadingId(null);
    }
  };

  const fetchCustomQuote = async () => {
    setCustomError(null);
    setCustomQuote(null);
    setCustomBusy(true);
    try {
      const { data } = await api.get("/caul/topup/stripe-quote-for-caul", { params: { caul: Number(customCaul) } });
      setCustomQuote(data);
    } catch (e) {
      setCustomError(e.response?.data?.detail || "Could not fetch live price");
    } finally {
      setCustomBusy(false);
    }
  };

  const startCustomCheckout = async () => {
    setCustomBusy(true);
    try {
      const { data } = await api.post("/checkout/init", {
        kind: "caul_topup",
        caulAmount: Number(customCaul),
        origin: window.location.origin,
      });
      if (!data.url) throw new Error("No checkout URL");
      if (data.cryptoEnabled === false) setCryptoEnabled(false);
      gaTrack.caulTopupInitiated({ method: "stripe", caulAmount: Number(customCaul), pricingMode: "live" });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setCustomBusy(false);
    }
  };

  return (
    <div data-testid="caul-topup-stripe">
      {statusBusy && (
        <div className="mb-3 text-xs text-primary flex items-center gap-2" data-testid="caul-topup-polling">
          <Loader2 size={12} className="animate-spin" /> Confirming your payment with Stripe…
        </div>
      )}

      <div className="inline-flex rounded-sm border border-secondary/20 mb-4 overflow-hidden text-[11px]">
        <button
          type="button"
          onClick={() => setMode("package")}
          className={`px-3 py-1.5 uppercase tracking-[0.18em] ${mode === "package" ? "bg-secondary/20 text-foreground" : "text-foreground/55 hover:bg-secondary/[0.06]"}`}
          data-testid="caul-topup-stripe-mode-package"
        >
          Packages
        </button>
        <button
          type="button"
          onClick={() => setMode("custom")}
          className={`px-3 py-1.5 uppercase tracking-[0.18em] ${mode === "custom" ? "bg-secondary/20 text-foreground" : "text-foreground/55 hover:bg-secondary/[0.06]"}`}
          data-testid="caul-topup-stripe-mode-custom"
        >
          Custom amount (live price)
        </button>
      </div>

      {mode === "package" && packages.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {packages.map((p) => {
            const busy = loadingId === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => startCheckout(p.id)}
                disabled={!!loadingId}
                className="text-left rounded-sm border border-secondary/30 bg-secondary/[0.04] hover:border-secondary hover:bg-secondary/[0.08] transition-colors p-4 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid={`caul-topup-${p.id}`}
              >
                <div className="label-mystic text-secondary/80">{p.label}</div>
                <div className="font-serif text-3xl text-foreground mt-1">
                  {p.caul}
                  <span className="text-foreground/40 text-sm ml-1">CAUL</span>
                </div>
                <div className="text-xs text-foreground/65 mt-1">
                  ${p.usd.toFixed(2)} · {cryptoEnabled ? "card or USDC" : "card"}
                </div>
                {busy && (
                  <div className="text-xs text-primary mt-2 flex items-center gap-1">
                    <Loader2 size={11} className="animate-spin" /> Redirecting…
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {mode === "custom" && (
        <div className="rounded-sm border border-secondary/20 p-4" data-testid="caul-topup-custom">
          <label className="label-mystic text-secondary/80 block mb-2">CAUL amount</label>
          <div className="flex items-stretch gap-2">
            <input
              type="number"
              min={100}
              step={50}
              value={customCaul}
              onChange={(e) => setCustomCaul(e.target.value)}
              className="parchment-input flex-1 px-3 py-2 rounded-sm border border-secondary/30 bg-background text-foreground text-base"
              placeholder="1000"
              data-testid="caul-topup-custom-input"
            />
            <button
              type="button"
              onClick={fetchCustomQuote}
              disabled={customBusy || !customCaul || Number(customCaul) <= 0}
              className="px-4 py-2 rounded-sm bg-secondary text-secondary-foreground text-xs uppercase tracking-[0.18em] hover:bg-secondary/85 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="caul-topup-custom-quote-btn"
            >
              {customBusy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Live price
            </button>
          </div>

          {customQuote && (
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="label-mystic text-secondary/70 text-[10px]">You receive</div>
                <div className="font-serif text-2xl text-foreground" data-testid="caul-topup-custom-quote-caul">
                  {Number(customQuote.caulAmount).toLocaleString()}
                  <span className="text-foreground/40 text-xs ml-1">CAUL</span>
                </div>
              </div>
              <div>
                <div className="label-mystic text-secondary/70 text-[10px]">You pay</div>
                <div className="font-serif text-2xl text-foreground" data-testid="caul-topup-custom-quote-usd">
                  ${Number(customQuote.usd).toFixed(2)}
                </div>
              </div>
              <div className="col-span-2 text-[11px] text-foreground/55">
                @ ${Number(customQuote.caulUsdPrice).toFixed(6)} / CAUL · {Number(customQuote.spreadPct).toFixed(1)}% spread
              </div>
              <button
                type="button"
                onClick={startCustomCheckout}
                disabled={customBusy}
                className="col-span-2 mt-1 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em] hover:bg-primary/85 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="caul-topup-custom-checkout-btn"
              >
                {customBusy ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />}
                Pay ${Number(customQuote.usd).toFixed(2)} with card or USDC
              </button>
            </div>
          )}

          {customError && (
            <div className="mt-3 text-xs text-destructive" data-testid="caul-topup-custom-error">{customError}</div>
          )}
        </div>
      )}

      {/* Trust strip — official Stripe + USDC marks reassure crypto-native users */}
      <div
        className="mt-4 pt-3 border-t border-secondary/15 flex items-center gap-3 text-[11px] text-foreground/55"
        data-testid="caul-topup-trust-strip"
      >
        <span className="uppercase tracking-[0.18em]">Secured by</span>
        <svg
          aria-label="Stripe"
          viewBox="0 0 60 25"
          className="h-3.5 w-auto"
          fill="#635BFF"
          role="img"
        >
          <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.63l.21 1.03c.56-.52 1.58-1.27 3.16-1.27 2.83 0 5.5 2.54 5.5 7.22 0 5.11-2.64 7.75-5.46 7.75zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.27 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.47 1.06zm-8.95 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.14l.01 5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.58-.24 1.58-1C5.92 13.78 0 14.51 0 10.13 0 7.2 2.24 5.44 5.57 5.44c1.4 0 2.8.21 4.2.77v3.88c-1.28-.7-2.91-1.09-4.2-1.09-.86 0-1.4.25-1.4.91 0 1.84 5.96.96 5.96 5.74z" />
        </svg>
        <span className="text-foreground/30">·</span>
        <span className="inline-flex items-center gap-1.5">
          <svg
            aria-label="USDC"
            viewBox="0 0 32 32"
            className="h-4 w-4"
            role="img"
          >
            <circle cx="16" cy="16" r="16" fill="#2775CA" />
            <path
              d="M20.5 18.1c0-2.4-1.4-3.2-4.3-3.5-2.1-.3-2.5-.8-2.5-1.7s.6-1.5 1.9-1.5c1.2 0 1.8.4 2.1 1.3.06.18.24.31.43.31h1.16c.24 0 .43-.19.43-.43v-.05c-.24-1.36-1.34-2.4-2.75-2.53V8.4c0-.24-.19-.43-.43-.43h-1.07c-.24 0-.43.19-.43.43v1.59c-2.1.29-3.43 1.7-3.43 3.45 0 2.27 1.36 3.14 4.27 3.49 1.96.32 2.59.78 2.59 1.78 0 1-.86 1.69-2.04 1.69-1.6 0-2.16-.68-2.35-1.61a.45.45 0 0 0-.43-.34h-1.24a.43.43 0 0 0-.43.43v.05c.29 1.49 1.27 2.56 3.57 2.85v1.62c0 .24.19.43.43.43h1.07c.24 0 .43-.19.43-.43v-1.62c2.1-.34 3.45-1.78 3.45-3.65z"
              fill="#fff"
            />
            <path
              d="M12.7 25.5c-5.5-2-8.3-8.1-6.2-13.5 1.1-3 3.4-5.3 6.2-6.4.27-.13.4-.32.4-.65V4c0-.27-.13-.45-.4-.5-.07 0-.2 0-.27.05a13.13 13.13 0 0 0 0 24.9c.07.05.2.05.27.05.27-.05.4-.23.4-.5v-.95c0-.27-.13-.45-.4-.55zm6.87-22c-.07-.05-.2-.05-.27-.05-.27.05-.4.23-.4.5v.95c0 .27.13.45.4.55 5.5 2 8.3 8.1 6.2 13.5-1.1 3-3.4 5.3-6.2 6.4-.27.13-.4.32-.4.65v.95c0 .27.13.45.4.5.07 0 .2 0 .27-.05a13.13 13.13 0 0 0 0-24.9z"
              fill="#fff"
            />
          </svg>
          <span>USDC supported</span>
        </span>
      </div>
    </div>
  );
}
